import BaseCard from 'Prototype/Level/Card/BaseCard';
import CardComponent from 'Component/Level/Card';
import TransformComponent from 'Component/Transform';
import CardSystem, { getCardHandler } from '../CardSystem';
import ITask from 'Utility/Task/ITask';
import { delay } from 'Utility/Task/TaskQueue';
import AnimationComponent from 'Component/Animation';
import { LevelActions } from 'State/Level/LevelActions';
import { StartAnimation, StartAnimationClip } from '../../Animation/AnimationHelper';
import { PlaySound } from '../../Audio/AudioHelper';
import { UIEvent } from '../../UI/BaseUISystem';
import GoldCard from 'Prototype/Level/Card/GoldCard';
import { getStackCardTargetX } from 'State/Card/CardStateHelper';
import { CardStackBuildUp } from 'System/Animation/Clip';
import ObservablePoint from 'Utility/ObservablePoint';
import { MAX_OPENED_CARDS_IN_STACK } from 'Constants/InGame';
import Renderable from 'Component/Renderable';
import PlinkoCard from 'Prototype/Level/Card/PlinkoCard';

export const LEVEL_START = 'levelStart';

export default class LevelIntroTask implements ITask<CardSystem> {
    public *execute(system: CardSystem): IterableIterator<number> {
        system.scene.emit(UIEvent.DISABLE_INTERACTIVITY);
        const cardsInPlay = system.getCardsInPlay();
        const cardsInStack = system.getCardsInStack();

        // Set initial card positions.
        cardsInPlay.forEach(card => {
            system.cardsContainer.addChild(card);

            const transform = card.getComponent(TransformComponent);
            const cardComponent = card.getComponent(CardComponent);
            transform.x = cardComponent.x;
            transform.y = cardComponent.y - 1024;
            transform.rotation = cardComponent.rotation - Math.PI * 0.25;

            card.getComponent(Renderable).alpha = card instanceof PlinkoCard ? 0 : 1;

            getCardHandler(card).setCardFaceUp(false);
        });

        const cardsToSkip: number = Math.max(0, cardsInStack.length - MAX_OPENED_CARDS_IN_STACK);

        cardsInStack.forEach((card, index) => {
            system.stackContainer.addChild(card);

            const transform = card.getComponent(TransformComponent);
            transform.x = getStackCardTargetX(index) + 20;
            transform.y = 300;
            transform.skew = new ObservablePoint(
                () => {},
                transform,
                -0.04,
                0.52
            );
            transform.rotation = 0.52;
        });

        // First sort cardsInPlay for animation sequencing.
        cardsInPlay.sort((cardA: BaseCard, cardB: BaseCard) => {
            const cardAComponent = cardA.getComponent(CardComponent);
            const cardBComponent = cardB.getComponent(CardComponent);
            return cardAComponent.sequence - cardBComponent.sequence;
        });

        PlaySound('cards_added_level-start');

        for (let i = 0; i < cardsInPlay.length; i += 1) {
            const card = cardsInPlay[i];
            const cardComponent = card.getComponent(CardComponent);
            StartAnimation(card, 'CardFallIn', {
                x: cardComponent.x,
                y: cardComponent.y,
                rotation: cardComponent.rotation,
            });

            // Delay between each card sliding in.
            yield* delay(
                getDelayMs({
                    totalCards: cardsInPlay.length,
                    maxTotalTimeMs: 1500,
                    defaultDelayMs: 50,
                }),
            );
        }

        yield* this.waitForAnimations(cardsInPlay);

        PlaySound('booster-five-cards-use');

        for (let i = 0; i < cardsInStack.length; i += 1) {
            const card = cardsInStack[i];
            const target = system.cardStack.getCardTarget(card.id);

            StartAnimationClip(
                new CardStackBuildUp(card, {
                    x: target.x,
                    y: target.y,
                })
            );

            if (i >= cardsToSkip) {
                yield* delay(
                    getDelayMs({
                        totalCards: MAX_OPENED_CARDS_IN_STACK,
                        maxTotalTimeMs: 1100,
                        defaultDelayMs: 40,
                    }),
                );
            }
        }

        yield* delay(100);

        // Get cards that are to be revealed after being dealt to the board.
        const cardsLeftToRight = new Array<BaseCard>();
        for (const card of cardsInPlay) {
            const cardComponent = card.getComponent(CardComponent);
            if (cardComponent.faceUp) {
                cardsLeftToRight.push(card);
            }
        }

        // Sort cards from left to right for consistent reveal animation.
        cardsLeftToRight.sort((cardA: BaseCard, cardB: BaseCard) => {
            const cardATransform = cardA.getComponent(TransformComponent);
            const cardBTransform = cardB.getComponent(TransformComponent);
            return cardATransform.x - cardBTransform.x;
        });

        const shouldFlip = (card: BaseCard) => Boolean(
            // Don't flip gold cards
            !(card instanceof GoldCard) &&
            // And skip flipping animation if the card is completely covered by some other card
            !isCardCovered(card, cardsLeftToRight)
        );

        const delayMsFlipOpenCards = getDelayMs({
            totalCards: cardsLeftToRight.filter(shouldFlip).length,
            maxTotalTimeMs: 2000,
            defaultDelayMs: 100,
        });
        for (let i = 0; i < cardsLeftToRight.length; i += 1) {
            const card = cardsLeftToRight[i];
            if (!shouldFlip(card)) {
                // Still make sure they're facing up
                getCardHandler(card).setCardFaceUp(true);
            } else {
                StartAnimation(card, 'CardFlip', { faceUp: true });

                // Wait between each card flip.
                yield* delay(delayMsFlipOpenCards);
            }
        }

        yield* this.waitForAnimations(cardsLeftToRight);

        cardsInPlay.forEach(card => {
            if (card instanceof GoldCard) {
                card.startIdleAnimation();
            }
        });

        LevelActions.levelActive();
    }

    private *waitForAnimations(cards: BaseCard[]): IterableIterator<number> {
        // Wait for animations.
        for (const card of cards) {
            const animation = card.getComponent(AnimationComponent);
            while (animation.isPlaying) {
                yield;
            }
        }
    }
}

function getDelayMs(
    params: {
        totalCards: number,
        maxTotalTimeMs: number,
        defaultDelayMs: number,
    },
): number {
    return Math.min(
        params.defaultDelayMs,
        params.maxTotalTimeMs / params.totalCards,
    );
}

function isCardCovered(card: BaseCard, cards: Array<BaseCard>, tolerance: number = 3) {
    const cardComponent = card.getComponent(CardComponent);
    return cards.some(otherCard => {
        if (otherCard === card) return false;
        const otherCardComponent = otherCard.getComponent(CardComponent);
        if (otherCardComponent.depth <= cardComponent.depth) return false;
        if (otherCardComponent.angle !== cardComponent.angle) return false;
        const dx = otherCardComponent.x - cardComponent.x;
        const dy = otherCardComponent.y - cardComponent.y;
        const fullyCovered = dx * dx + dy * dy <= tolerance * tolerance;
        return fullyCovered;
    });
}
